import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { RotateCcw, CheckCircle2, XCircle, PackageCheck, Plus, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { key: 'all', tkey: 'admin.returns_page.filter_all', fallback: 'Všetky', color: 'bg-gray-100 text-gray-800' },
  { key: 'REQUESTED', tkey: 'admin.returns_page.filter_requested', fallback: 'Žiadosti', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'APPROVED', tkey: 'admin.returns_page.filter_approved', fallback: 'Schválené', color: 'bg-blue-100 text-blue-800' },
  { key: 'RECEIVED', tkey: 'admin.returns_page.filter_received', fallback: 'Prijaté', color: 'bg-green-100 text-green-800' },
  { key: 'REJECTED', tkey: 'admin.returns_page.filter_rejected', fallback: 'Zamietnuté', color: 'bg-red-100 text-red-800' },
];

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded ${s.color}`}>
      {t(s.tkey, s.fallback)}
    </span>
  );
};

const AdminReturns = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rmas, setRmas] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ order_id: '', reason: '', notes: '' });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await fetch(`${API_URL}/api/admin/rmas${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRmas(data.rmas || []);
        setCounts(data.counts || {});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const createRma = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rmas`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: createForm.order_id.trim(),
          reason: createForm.reason,
          notes: createForm.notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data.detail || t('admin.returns_page.create_error', 'Chyba pri vytváraní RMA'));
        return;
      }
      setShowCreate(false);
      setCreateForm({ order_id: '', reason: '', notes: '' });
      fetchData();
    } finally {
      setCreateLoading(false);
    }
  };

  const doAction = async (rmaId, action) => {
    setActionLoading(rmaId + action);
    try {
      const res = await fetch(`${API_URL}/api/admin/rmas/${rmaId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Chyba');
      }
      fetchData();
    } finally {
      setActionLoading('');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('sk-SK');
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-returns-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <RotateCcw className="h-7 w-7 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t('admin.returns.title', 'Vrátenia tovaru (RMA)')}
          </h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
          data-testid="admin-rma-create-btn"
        >
          <Plus className="h-4 w-4" />
          {t('admin.returns.new', 'Nová RMA')}
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            data-testid={`admin-rma-filter-${s.key.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition border ${
              statusFilter === s.key
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
          >
            {t(s.tkey, s.fallback)}
            {s.key !== 'all' && counts[s.key] !== undefined && (
              <span className="ml-2 text-xs opacity-75">({counts[s.key]})</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
        </div>
      ) : rmas.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
          <RotateCcw className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <div>{t('admin.returns.empty', 'Žiadne vrátenia.')}</div>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">{t('admin.returns_page.col_date', 'Dátum')}</th>
                <th className="px-4 py-3 text-left">{t('admin.returns_page.col_customer', 'Zákazník')}</th>
                <th className="px-4 py-3 text-left">{t('admin.returns_page.col_order', 'Objednávka')}</th>
                <th className="px-4 py-3 text-right">{t('admin.returns_page.col_return', 'Vrátenie')}</th>
                <th className="px-4 py-3 text-left">Stav</th>
                <th className="px-4 py-3 text-right">Akcie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rmas.map((r) => (
                <tr key={r.id} data-testid={`admin-rma-row-${r.number}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">{r.number}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.customer_name || '—'}</div>
                    <div className="text-xs text-gray-500">{r.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                    {r.order_id?.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {r.refund_amount_eur?.toFixed(2)} {r.currency || 'EUR'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {r.status === 'REQUESTED' && (
                        <>
                          <button
                            onClick={() => doAction(r.id, 'approve')}
                            disabled={actionLoading === r.id + 'approve'}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                            data-testid={`admin-rma-approve-${r.number}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> {t('admin.returns_page.approve', 'Schváliť')}
                          </button>
                          <button
                            onClick={() => doAction(r.id, 'reject')}
                            disabled={actionLoading === r.id + 'reject'}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                          >
                            <XCircle className="h-3.5 w-3.5" /> {t('admin.returns_page.reject', 'Zamietnuť')}
                          </button>
                        </>
                      )}
                      {r.status === 'APPROVED' && (
                        <>
                          <button
                            onClick={() => doAction(r.id, 'receive')}
                            disabled={actionLoading === r.id + 'receive'}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100"
                            data-testid={`admin-rma-receive-${r.number}`}
                          >
                            <PackageCheck className="h-3.5 w-3.5" /> {t('admin.returns_page.receive', 'Prijať na sklad')}
                          </button>
                          <button
                            onClick={() => doAction(r.id, 'reject')}
                            disabled={actionLoading === r.id + 'reject'}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                          >
                            <XCircle className="h-3.5 w-3.5" /> {t('admin.returns_page.reject', 'Zamietnuť')}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expandable details of latest RMA */}
      {rmas.length > 0 && (
        <div className="mt-4 text-xs text-gray-500">
          {t('admin.returns_page.tip', 'Tip: pri <b>Prijať na sklad</b> sa tovar automaticky vracia na sklad. Refundácia sa vykonáva ručne prevodom.')}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.returns.new', 'Nová RMA')}
              </h2>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={createRma} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.returns_page.order_id', 'ID objednávky')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={createForm.order_id}
                  onChange={(e) => setCreateForm({ ...createForm, order_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  placeholder="44d80a9b-..."
                  data-testid="admin-rma-order-id-input"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {t('admin.returns_page.order_state_hint', 'Objednávka musí byť v stave INVOICED / SHIPPED / DELIVERED.')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.returns_page.reason', 'Dôvod')}
                </label>
                <input
                  type="text"
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder={t('admin.returns_page.reason_ph', 'napr. nesprávna farba, poškodený tovar…')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.returns_page.note', 'Poznámka')}
                </label>
                <textarea
                  rows={3}
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              {createError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{createError}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {t('common.cancel', 'Zrušiť')}
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
                  data-testid="admin-rma-create-submit"
                >
                  {createLoading ? t('admin.returns_page.creating', 'Vytváram…') : t('admin.returns_page.create_rma', 'Vytvoriť RMA')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReturns;
